import React, { useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
} from '../../../common/components/sections'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { treeOptionPropType } from '../../../common/prop-types'

import { useUpdateDishTypeAlternativeIngredientsDetailsMutation } from '../../hooks'
import { dishTypePropType } from '../../prop-types'
import { setDishTypePageIsDirty } from '../../redux/actions'
import { DishTypeAlternativeIngredientsFormList } from '../sections'

const DishTypeAlternativeIngredientsDetailsForm = ({
  dishType,
  ingredientOptions,
  groupedFlatIngredientTreeDataByValue,
  flatIngredientTreeData,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.DISH_TYPE.SAVE_ALTERNATIVE_INGREDIENTS,
  ])

  const [
    updateDishTypeAlternativeIngredientsDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateDishTypeAlternativeIngredientsDetailsMutation()

  const handleSaveData = useCallback(
    data => updateDishTypeAlternativeIngredientsDetails(dishType.id, data),
    [dishType.id, updateDishTypeAlternativeIngredientsDetails],
  )

  useEffect(() => {
    resetForm()
  }, [dishType.alternativeIngredients, resetForm])

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Dish Type details error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    dispatch(setDishTypePageIsDirty({ isAlternativeIngredientsDirty: isDirty }))
  }, [dispatch, isDirty])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={dishType}
      name="additionalIngredientsForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Row gutter={[16]}>
        <Col span={18}>
          <DishTypeAlternativeIngredientsFormList
            disabled={!isFormEnabled}
            ingredientOptions={ingredientOptions}
            groupedFlatIngredientTreeDataByValue={
              groupedFlatIngredientTreeDataByValue
            }
            flatIngredientTreeData={flatIngredientTreeData}
            initialValues={dishType.alternativeIngredients}
            form={form}
            isDirty={isDirty}
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.DISH_TYPE.SAVE_ALTERNATIVE_INGREDIENTS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

DishTypeAlternativeIngredientsDetailsForm.propTypes = {
  dishType: dishTypePropType.isRequired,
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType).isRequired,
  groupedFlatIngredientTreeDataByValue: PropTypes.object.isRequired,
  flatIngredientTreeData: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default DishTypeAlternativeIngredientsDetailsForm
